import { default as Skeleton } from 'antd/es/skeleton';
import { default as Tag } from 'antd/es/tag';
import { default as Modal } from 'antd/es/modal';
import { useEffect, useMemo, useState } from 'react';
import { getCurrentEnv } from '../utils/getCurrentEnv';
import { PrismCode } from './PrismCode';

import { default as Button } from 'antd/es/button';
import { useUser } from '../hooks/useUser';
import { fetchOrganisation } from '../network/fetchOrganisation';

import { generateCertificates } from '../utils/generate-csr-certificates';
import { downloadFile } from '../utils/downloadFile';
import { goToCustomerApp } from '../utils/goToCustomerApp';

export function InpayPrivateAndCertificatePairKeyModal() {
  const { currentOrganisation } = useUser();

  const CURRENT_ENV = useMemo(() => getCurrentEnv(window.location.host), []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!currentOrganisation)
    return (
      <Button
        size="large"
        data-testid="log-in-from-products"
        onClick={() => goToCustomerApp()}
        type="primary"
      >
        Log in to download your private key and CSR
      </Button>
    );

  return (
    <>
      <Button
        type="primary"
        size="large"
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        Download your private key pair and your certificate signing request
      </Button>

      <Modal
        title={
          <div className="flex items-center justify-center">
            <div className="flex w-full flex-col gap-4">
              <div className="flex flex-row">
                <span>
                  {CURRENT_ENV !== 'prod' && (
                    <Tag bordered={false} className="bg-inpay-yellow-1000">
                      <p className="text-bold dark:text-black">{CURRENT_ENV}</p>
                    </Tag>
                  )}
                </span>

                <h2>Private key and a Certificate signing request (CSR)</h2>
              </div>

              <div className="warning">
                Copy the contents of your private key below to your application
                and store this securely. Copy the contents of the certificate
                signing request (csr) and send this to support@inpay.com to
                retrieve your API keys. <br /> <br /> The private key data will
                not be stored in our servers and will not longer be accessible
                once you close or refresh this page.
                <b> Please make sure you have a local copy of this key.</b>
              </div>
            </div>
          </div>
        }
        open={isModalOpen}
        width={720}
        onOk={() => setIsModalOpen(false)}
        onCancel={() => setIsModalOpen(false)}
      >
        <CertificateModalContent />
      </Modal>
    </>
  );
}

function CertificateModalContent() {
  const [keyPair, setKeyPair] = useState<{
    csr: string;
    privateKey: string;
  } | null>(null);

  const [loadingOrgDetail, setLoadingOrgDetail] = useState(false);

  const { currentOrganisation } = useUser();

  useEffect(() => {
    const getOrgDetail = async () => {
      setLoadingOrgDetail(true);

      if (currentOrganisation) {
        const organisation = await fetchOrganisation(
          currentOrganisation.id.toString()
        );
        if (organisation?.data) {
          const keyPair = await generateCertificates({
            orgDetail: organisation.data
          });

          setKeyPair(keyPair);
        }
      }
      setLoadingOrgDetail(false);
    };
    getOrgDetail();
  }, [currentOrganisation]);

  if (loadingOrgDetail && currentOrganisation) return <Skeleton />;

  return (
    <div className="flex flex-col gap-4">
      <h2>Certificate signing request</h2>
      <PrismCode showClipboard code={keyPair?.csr} size="small" />
      <div className="flex flex-col">
        <div className="space-between flex flex-row gap-2">
          <p className="font-bold">Name: </p>
          <code>{currentOrganisation?.name}</code>
        </div>
        <div className="space-between flex flex-row gap-2">
          <p className="font-bold">Country code: </p>
          <code>{currentOrganisation?.country_code}</code>
        </div>
        <div className="space-between flex flex-row gap-2">
          <p className="font-bold">State: </p>
          <code>
            {new Intl.DisplayNames(['en'], { type: 'region' }).of(
              currentOrganisation?.country_code ?? ''
            )}
          </code>
        </div>
        <div className="space-between flex flex-row gap-2">
          <p className="font-bold">City: </p>
          <code>{currentOrganisation?.city}</code>
        </div>
      </div>
      <Button
        type="primary"
        onClick={() => {
          if (keyPair?.csr) {
            downloadFile(keyPair.csr, 'merchant.csr');
          }
        }}
      >
        Click here to download the certificate signing request
      </Button>
      <h2>Private key</h2>
      <PrismCode showClipboard code={keyPair?.privateKey} size="small" />
      <Button
        type="primary"
        onClick={() => {
          if (keyPair?.privateKey) {
            downloadFile(keyPair.privateKey, 'merchant-private.key');
          }
        }}
      >
        Click here to download the private key
      </Button>
    </div>
  );
}
